import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Navigate, Link } from "react-router-dom";
import MainWrapper from "../../MainWrapper";
import { Table, Spinner, Modal, Button, Form } from "react-bootstrap";
import Icon from "react-icons-kit";
import { ic_arrow_back } from "react-icons-kit/md";
import Loader from "../../layouts/Loader";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../../../config";

// Drill-down view opened from the "Potential Lost Customers" dashboard widget.
//
// On mount it bulk-acknowledges the active alerts in scope (so the widget's
// flames clear), then lists them: Property Name, Salesperson, Last Order Date.
// Respects the role's dataScope ("self" => only the logged-in salesperson's
// properties), read from the role-permission payload the sidebar already loads.
const PotentialLostCustomers = ({
  auth: { auth, isAuthenticated },
  Admin: { userMenuList },
}) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  // "Reason for losing" options for the acknowledge modal.
  const [reasons, setReasons] = useState([]);
  // The alert row whose "Acknowledge Lost Customer" modal is open (null = closed).
  const [modalRow, setModalRow] = useState(null);
  const [reasonId, setReasonId] = useState("");
  const [notes, setNotes] = useState("");
  // True while the AddAcknowledgement POST is in flight (disables the modal).
  const [submitting, setSubmitting] = useState(false);

  const token = auth && auth[0] ? auth[0].token : null;
  const userId = auth && auth[0] ? auth[0].userId : null;

  const dashboard =
    userMenuList && userMenuList[0] ? userMenuList[0].dashboard : null;
  const dataScope = (dashboard && dashboard.dataScope) || "all";
  let tmpSalesId = userId;
  if(auth && (auth[0]?.roleName ==='Sales' && auth[0]?.salesPersonId !=='')){
    tmpSalesId = auth[0]?.salesPersonId
  }
  const scopedSalesPersonId = dataScope === "self" ? tmpSalesId : null;

  const priorityStyle = { backgroundColor: '#BBDEFB', color: '#0D47A1', cursor: 'pointer' };

  useEffect(() => {
    if (!token) return;
    setLoading(true);

    const ackParams = { userId };
    if (scopedSalesPersonId) ackParams.salesPersonId = scopedSalesPersonId;
    const getParams = { userId };
    if (scopedSalesPersonId) getParams.salesPersonId = scopedSalesPersonId;

    // Acknowledge first (clears the widget flames), then load the list.
    axios
      .post(`${API_URL}/User/api/LostCustomerParameter/AcknowledgeAllAlerts`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: ackParams,
      })
      .catch((err) => {
        console.warn("Could not acknowledge lost-customer alerts:", err.message);
      })
      .finally(() => {
        axios
          .get(`${API_URL}/User/api/LostCustomerParameter/GetAlerts`, {
            headers: { Authorization: `Bearer ${token}` },
            params: getParams,
          })
          .then((res) => {
            const rows = Array.isArray(res.data)
              ? res.data
              : (res.data && res.data.result) || [];
            setAlerts(rows);
          })
          .catch((err) => {
            console.warn("Could not load potential lost customers:", err.message);
          })
          .finally(() => setLoading(false));
      });
  }, [token, scopedSalesPersonId]);

  // Load the "Reason for losing" dropdown options once (active reasons, already
  // returned in display order by the backend).
  useEffect(() => {
    if (!token) return;
    let active = true;
    axios
      .get(`${API_URL}/User/api/User/GetAcknowledgementReasons`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data && res.data.result) || [];
        setReasons(
          [...rows].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
        );
      })
      .catch((err) => {
        console.warn("Could not load acknowledgement reasons:", err.message);
      });
    return () => {
      active = false;
    };
  }, [token]);

  if (!isAuthenticated) return <Navigate to="/login" />;

  // A customer flagged as a Priority is highlighted in red. NOTE: the priority
  // flag is not yet part of the GetAlerts payload — this helper stays dormant
  // (returns false) until the backend adds it. Wire the real field here once the
  // API returns it (e.g. `row.isPriority`).
  const isPriorityRow = (row) => row.isPriority === true;

  // Per-row "Acknowledge" opens the "Acknowledge Lost Customer" modal so the rep
  // can capture a reason (and optional notes) before acknowledging.
  const openAcknowledgeModal = (row) => {
    setModalRow(row);
    setReasonId("");
    setNotes("");
  };

  const closeAcknowledgeModal = () => {
    if (submitting) return;
    setModalRow(null);
  };

  // Reads the API's error message (400 responses carry it in result[0]).
  const extractErrorMessage = (err) => {
    const result = err && err.response && err.response.data && err.response.data.result;
    if (Array.isArray(result) && typeof result[0] === "string") return result[0];
    return "Could not acknowledge. Please try again.";
  };

  // Submit the modal: POST AddAcknowledgement with the chosen reason + notes,
  // then drop the acknowledged row from the list on success.
  const handleAcknowledgeSubmit = (e) => {
    e.preventDefault();
    if (!token || submitting || !modalRow) return;
    if (!reasonId) {
      toast.error("Reason for losing is required.");
      return;
    }
    const row = modalRow;
    setSubmitting(true);
    const payload = {
      contactInfoId: row.contactInfoId,
      userId,
      reasonId: Number(reasonId),
      notes,
    };
    axios
      .post(`${API_URL}/User/api/User/AddAcknowledgement`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        toast.success("Acknowledged.");
        setAlerts((prev) =>
          prev.filter((a) => a.lostCustomerAlertId !== row.lostCustomerAlertId)
        );
        setModalRow(null);
      })
      .catch((err) => {
        console.warn("Could not acknowledge customer:", err.message);
        toast.error(extractErrorMessage(err));
      })
      .finally(() => setSubmitting(false));
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const year = d.getFullYear();
    return `${month < 10 ? "0" + month : month}-${day < 10 ? "0" + day : day}-${year}`;
  };

  return (
    <Fragment>
      <MainWrapper>
        <Loader loading={loading}>
          <div className="container">
            <div className="sub-header mt-5 mb-3">
              <h2>Potential Lost Customers</h2>
              <Link className="btn btn-primary me-1" to="/dashboard">
                <Icon icon={ic_arrow_back} />Back
              </Link>
            </div>
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>Property Name</th>
                  <th>Salesperson</th>
                  <th>Last Order Date</th>
                  <th>Days Since Last Order</th>
                  <th>Acknowledge</th>
                </tr>
              </thead>
              <tbody>
                {alerts.length > 0 ? (
                  alerts.map((row) => (
                    <tr
                      key={row.lostCustomerAlertId}
                      className={isPriorityRow(row) ? "table-danger" : ""}
                      style={row.priorityNumber ? {...priorityStyle } : {}}
                    >
                      <td style={row.priorityNumber ? {...priorityStyle } : {}}>{row.propertyName || "—"}</td>
                      <td style={row.priorityNumber ? {...priorityStyle } : {}}>{row.salesPersonName || "—"}</td>
                      <td style={row.priorityNumber ? {...priorityStyle } : {}}>{formatDate(row.lastOrderDate)}</td>
                      <td style={row.priorityNumber ? {...priorityStyle } : {}}>{row.daysSinceLastOrder}</td>
                      <td style={row.priorityNumber ? {...priorityStyle } : {}}>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => openAcknowledgeModal(row)}
                        >
                          Acknowledge
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center">
                      No potential lost customers.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <Modal show={modalRow != null} onHide={closeAcknowledgeModal} centered>
            <Form onSubmit={handleAcknowledgeSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>Acknowledge Lost Customer</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Reason for Losing</Form.Label>
                  <Form.Select
                    value={reasonId}
                    onChange={(e) => setReasonId(e.target.value)}
                  >
                    <option value="">Select a reason…</option>
                    {reasons.map((r) => (
                      <option
                        key={r.lostCustomerReasonId}
                        value={r.lostCustomerReasonId}
                      >
                        {r.reasonName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Notes:</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={closeAcknowledgeModal}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={submitting}>
                  {submitting ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    "Save"
                  )}
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>
        </Loader>
      </MainWrapper>
    </Fragment>
  );
};

const mapStateToProps = ({ auth, Admin }) => ({ auth, Admin });

export default connect(mapStateToProps)(PotentialLostCustomers);
